import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { isEmpty } from "lodash";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { Profile, ProfileFormData, UserEmailData } from "src/app/models/models";
import { clearAvatarCache, warmAvatarCache } from "src/app/shared/utils/avatar-cache.utils";
import { HelperService } from "src/app/util/helper.service";
import { SESSION } from "src/session.config";
import { HttpService } from "./http.service";
import { LoaderService } from "./loader.service";
import { RoleService } from "./role.service";

@Injectable({
	providedIn: "root",
})
export class ProfileService implements OnInit, OnDestroy {
	private _profilePreview?: string;
	private _profilePreviewSubject?: BehaviorSubject<string | undefined>;
	private _signaturePreview?: string = "";
	private _signatureFile?: File;

	emailDataSub?: Subscription;
	profileSub?: Subscription;

	private _profile?: Profile;
	private userId?: string;
	private profileRequestGeneration = 0;

	private _profileLoading: boolean = false;
	private _hasProfile: boolean = true;

	constructor(
		private roleService: RoleService,
		private loaderService: LoaderService,
		private httpService: HttpService,
		private helperService: HelperService,
	) {
		this.loadCachedProfile();
	}

	private loadCachedProfile() {
		try {
			const cached = localStorage.getItem(SESSION.SESSION_KEY_PROFILE);
			if (!cached) return;

			const parsed = JSON.parse(cached) as Profile;
			if (parsed) {
				this.setProfile(parsed, false);
			}
		} catch {
			// ignore cache parse issues
			localStorage.removeItem(SESSION.SESSION_KEY_PROFILE);
		}
	}

	initUser(setLoading: boolean = false) {
		this.loaderService.setLoading(setLoading);

		if (!navigator.onLine) {
			// When offline rely on cached profile (if any)
			this.loaderService.setLoading(false);
			return;
		}

		this.cancelPendingProfileRequests();
		const generation = ++this.profileRequestGeneration;

		const userDetails = this.roleService.userDetails;

		if (!isEmpty(userDetails)) {
			const userEmail = userDetails.email;

			if (!isEmpty(userEmail)) {
				this.emailDataSub = this.httpService.getUserByEmail(userEmail).subscribe((result) => {
					if (generation !== this.profileRequestGeneration) {
						return;
					}

					const emailUserData = result.data as UserEmailData;
					const isProfileSetup = emailUserData.isProfileSetup;
					const userId = emailUserData.id;
					this.userId = userId;

					this._hasProfile = isProfileSetup;

					if (isProfileSetup) {
						this.profileSub = this.httpService.getProfile(userId).subscribe((result) => {
							if (generation !== this.profileRequestGeneration) {
								return;
							}

							const profile = result.data as Profile;

							this.setProfile(profile);

							this.loaderService.setLoading(false);
						});
					} else {
						this.setProfile(undefined);
						this.loaderService.setLoading(false);
					}
				});
			}
		}
	}

	updateProfile(userId: string, profileData: ProfileFormData, setLoading: boolean = true) {
		this.loaderService.setLoading(setLoading);
		this.httpService.updateProfile(userId, profileData).subscribe(
			(result) => {
				this.initUser(setLoading);
			},
			(error) => {
				console.error("updating failed:", error);
				this.loaderService.setLoading(false);
			},
		);
	}

	ngOnInit(): void {
		this.initUser();
	}

	ngOnDestroy(): void {
		this.cancelPendingProfileRequests();
	}

	setProfileLoading(loading: boolean) {
		this._profileLoading = loading;
	}

	get hasProfile(): boolean {
		return this._hasProfile;
	}

	private stripSecretKey(profile: Profile): Profile {
		const { secret_key: _secretKey, ...rest } = profile;
		return rest as Profile;
	}

	setProfile(profile?: Profile, persist: boolean = true): void {
		const sanitized = profile ? this.stripSecretKey(profile) : profile;
		this._profile = sanitized;

		this._profilePreview = sanitized?.image_signed_url || undefined;
		this._profilePreviewSubject?.next(this._profilePreview);
		if (this._profilePreview) {
			void warmAvatarCache(this._profilePreview);
		}

		const signatureData = sanitized?.signature?.data;
		this._signaturePreview = this.helperService.bufferDataToImageUrl(signatureData);
		this._signatureFile = signatureData ? this.helperService.bufferDataToFile(signatureData) : undefined;

		if (!persist) return;

		if (sanitized) {
			localStorage.setItem(SESSION.SESSION_KEY_PROFILE, JSON.stringify(sanitized));
		} else {
			localStorage.removeItem(SESSION.SESSION_KEY_PROFILE);
		}
	}

	/** Drop cached profile (localStorage + in-memory) on logout or identity change. */
	clearCachedProfile(): void {
		this.profileRequestGeneration++;
		this.cancelPendingProfileRequests();
		this.userId = undefined;
		this.setProfile(undefined);
		void clearAvatarCache();
	}

	private cancelPendingProfileRequests(): void {
		this.profileSub?.unsubscribe();
		this.profileSub = undefined;
		this.emailDataSub?.unsubscribe();
		this.emailDataSub = undefined;
	}

	get profileSubscription(): Subscription | undefined {
		return this.profileSub;
	}

	get profile(): Profile | undefined {
		return this._profile;
	}

	get profilePreview(): string | undefined {
		return this._profilePreview;
	}

	get profilePreview$(): Observable<string | undefined> {
		if (!this._profilePreviewSubject) {
			this._profilePreviewSubject = new BehaviorSubject<string | undefined>(this._profilePreview);
		}
		return this._profilePreviewSubject.asObservable();
	}

	get signaturePreview(): string | undefined {
		return this._signaturePreview;
	}

	get signatureFile(): File | undefined {
		return this._signatureFile;
	}

	get profileLoading(): boolean {
		return this._profileLoading;
	}
}
